import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api";
import {
  Box,
  Button,
  TextField,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Link,
  useMediaQuery,
} from "@mui/material";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";

// Matches Sunshine1's itdashboard theme (src/theme/appTheme.js, light mode) so this page looks
// consistent with the app HR/staff already use daily, even though it lives in a different repo with
// no shared theme module. Scoped to just this page (not TradeExpo's global theme/index.js) since
// nothing else in this app should be affected.
//
// htmlFontSize/fontSize correct for TradeExpo sharing the same `html { font-size: 62.5% }` reset as
// Sunshine1 (src/styles/base/_base.scss in both repos) - without this, MUI's rem-based sizing would
// render ~1.6x too large. Primary blue (#1976d2) matches MUI's own default in both repos' installed
// versions, set explicitly here so it can't drift if either app's MUI version ever changes it.
const s1Theme = createTheme({
  palette: { mode: "light", primary: { main: "#1976d2" } },
  typography: { htmlFontSize: 10, fontSize: 16 },
});

// S1- prefix (this file, the route, the BE router) is deliberate - stix_cust.contact_list has
// nothing to do with the existing Contacts/contacts component and "contact list" reads too similar
// to "contacts" otherwise. Public, no-header route (see AppRouter.js's hideHeaderRoutes) embedded via
// iframe in the FreshService KB portal.
//
// Access is a shared-secret query param, not real per-user auth - see s1PhoneDirectory.routes.js on
// the BE for the full rationale. The key must come in on this page's own URL (from the FreshService
// iframe's src) and is only ever forwarded to the BE as a header, never hardcoded here.
//
// telephone_number from the API is always the raw value as synced from Sunshine1 - formatting for
// display happens only in formatAuPhoneNumber() below, never persisted/sent anywhere formatted.
function formatAuPhoneNumber(raw, numberType) {
  if (!raw) return raw;
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("61") && digits.length === 11) {
    digits = `0${digits.slice(2)}`;
  }
  if (digits.length !== 10) return raw;
  return numberType === "mobile"
    ? `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
    : `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
}

const S1PhoneDirectoryContent = () => {
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key");

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const loadEntries = async () => {
      if (!key) {
        setError("This page requires a valid access link.");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const apiURL = (window.APP_CONFIG?.API_URL || "") + "s1-phone-directory";
        const response = await api.get(apiURL, { headers: { "X-S1-Key": key } });
        setEntries(response.data);
      } catch (e) {
        console.log("Error fetching S1 contact list:", e);
        setError("Unable to load the contact list.");
      } finally {
        setLoading(false);
      }
    };
    loadEntries();
  }, [key]);

  const departmentOptions = useMemo(
    () => [...new Set(entries.map((e) => e.department).filter(Boolean))].sort(),
    [entries]
  );
  const locationOptions = useMemo(
    () => [...new Set(entries.map((e) => e.office_location).filter(Boolean))].sort(),
    [entries]
  );

  const filteredEntries = useMemo(() => {
    const query = search.trim().toLowerCase();
    return entries.filter((entry) => {
      if (departmentFilter && entry.department !== departmentFilter) return false;
      if (locationFilter && entry.office_location !== locationFilter) return false;
      if (query) {
        const haystack = [entry.display_name, entry.job_title, entry.telephone_number, entry.department, entry.office_location, entry.email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [entries, search, departmentFilter, locationFilter]);

  return (
    <Box sx={{ position: "relative", minHeight: "100vh" }}>
      <title>Sunshine1 Contact List</title>

      {/* AppBar defaults to color="primary" - same theme.palette.primary.main (#1976d2) that
          Sunshine1's own top menu (MuiDrawer.js's <AppBar position="fixed">) uses, so this matches
          without hardcoding the colour twice. No nav/menu items - this page has nothing to navigate
          to, just the branding bar the user asked for. */}
      <AppBar position="static">
        <Toolbar sx={{ py: { xs: 1, sm: 1.5 }, justifyContent: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Sunshine Mitre10 - Contact List
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={isXs ? "column" : "row"}
          spacing={isXs ? 1 : 2}
          alignItems={isXs ? "stretch" : "center"}
          justifyContent="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: { xs: 1.5, sm: 2 } }}
        >
          <Button
            variant="outlined"
            size={isXs ? "small" : "medium"}
            onClick={() => {
              setSearch("");
              setDepartmentFilter("");
              setLocationFilter("");
            }}
            sx={{ flexShrink: 0 }}
          >
            Clear filters
          </Button>
          <TextField
            label="Search"
            placeholder="Name, job title, number, email, department, location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size={isXs ? "small" : "medium"}
            sx={{ flexGrow: 1, maxWidth: { sm: 320, md: 480 } }}
          />
          <FormControl size={isXs ? "small" : "medium"} sx={{ minWidth: 180 }}>
            <InputLabel id="s1pd-department-label">Department</InputLabel>
            <Select
              labelId="s1pd-department-label"
              label="Department"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <MenuItem value="">All departments</MenuItem>
              {departmentOptions.map((dept) => (
                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size={isXs ? "small" : "medium"} sx={{ minWidth: 180 }}>
            <InputLabel id="s1pd-location-label">Location</InputLabel>
            <Select
              labelId="s1pd-location-label"
              label="Location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <MenuItem value="">All locations</MenuItem>
              {locationOptions.map((loc) => (
                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {error && (
          <Box component="p" sx={{ color: "error.main", fontWeight: 600 }}>
            {error}
          </Box>
        )}
        {loading && <Box component="p">Loading directory...</Box>}

        {!loading && !error && (
          <Table size={isXs ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Location</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Display Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Department</TableCell>
                <TableCell sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>Phone Number</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries.map((entry, idx) => (
                <TableRow key={idx}>
                  <TableCell>{entry.office_location || "—"}</TableCell>
                  <TableCell>{entry.display_name}</TableCell>
                  <TableCell>{entry.job_title || "—"}</TableCell>
                  <TableCell>{entry.department || "—"}</TableCell>
                  {/* nowrap keeps a number on one line - the table then gives that column its full
                      content width and lets the free-text columns (job title, department) wrap
                      instead, which they do by default. */}
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatAuPhoneNumber(entry.telephone_number, entry.number_type)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={entry.number_type === "mobile" ? "Mobile" : "Landline"} />
                  </TableCell>
                  <TableCell>
                    {entry.email ? <Link href={`mailto:${entry.email}`}>{entry.email}</Link> : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {filteredEntries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">No matching entries.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
};

const S1PhoneDirectory = () => (
  <ThemeProvider theme={s1Theme}>
    <CssBaseline />
    <S1PhoneDirectoryContent />
  </ThemeProvider>
);

export default S1PhoneDirectory;
